import React, { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Button,
  Spinner,
  Badge,
  Table,
} from "reactstrap"
import toast from "toastr"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { get_crm_org, sync_crm_org } from "helpers/backend_helper"

const isOk = res => res?.statusCode === 200 || res?.StatusCode === 200

const CrmOrganizationDetail = () => {
  document.title = "CRM Organization Detail | TenderSoftware - Flexi TracBoard"

  const { id } = useParams()
  const name = decodeURIComponent(id)

  const [org, setOrg] = useState(null)
  const [loading, setLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)

  const loadOrg = useCallback(async () => {
    setLoading(true)
    try {
      const res = await get_crm_org(name)
      setOrg(res?.data || res?.Data || null)
    } catch (error) {
      toast.error(error?.message || "Failed to load organization detail")
      setOrg(null)
    } finally {
      setLoading(false)
    }
  }, [name])

  useEffect(() => {
    loadOrg()
  }, [loadOrg])

  const handleSync = async () => {
    setSyncLoading(true)
    try {
      const res = await sync_crm_org(name)
      if (isOk(res)) {
        toast.success(res?.message || res?.Message || "Organization synced to FlexiTrac")
      } else {
        toast.error(res?.message || res?.Message || "Failed to sync organization")
      }
    } catch (error) {
      toast.error(error?.message || "Failed to sync organization to FlexiTrac")
    } finally {
      setSyncLoading(false)
    }
  }

  const contactName = org
    ? [org.tsi_contact_first_name, org.tsi_contact_last_name].filter(Boolean).join(" ")
    : ""

  const row = (label, value) => (
    <tr>
      <th style={{ width: "30%" }}>{label}</th>
      <td>{value || value === 0 ? value : "-"}</td>
    </tr>
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={"CRM Organizations"} breadcrumbItem={"Organization Detail"} />
        <Row>
          <Col lg="8">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{org?.organization_name || name}</h5>
                <div className="d-flex gap-2">
                  <Link to="/crm-organizations" className="btn btn-outline-secondary btn-sm">
                    Back
                  </Link>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSync}
                    disabled={syncLoading || !org}
                  >
                    {syncLoading ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Syncing...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-sync me-1" /> Sync to FlexiTrac
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner /> Loading...
                  </div>
                ) : !org ? (
                  <div className="text-center p-5 text-muted">
                    Organization not found.
                  </div>
                ) : (
                  <Table bordered className="mb-0">
                    <tbody>
                      {row("Organization Name", org.organization_name)}
                      {row("ERP Id", org.name)}
                      {row("Country", org.tsi_country)}
                      <tr>
                        <th>Status</th>
                        <td>
                          {org.tsi_client_status === "Active Client" ? (
                            <Badge color="success">Active</Badge>
                          ) : (
                            <Badge color="secondary">
                              {org.tsi_client_status || "Inactive"}
                            </Badge>
                          )}
                        </td>
                      </tr>
                      {row("Contact", contactName)}
                      {row("Contact Email", org.tsi_contact_email)}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CrmOrganizationDetail
